import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    id: 'welcome',
    emoji: '👋',
    title: 'Bienvenue sur OneBotAds !',
    subtitle: 'Gérez toutes vos campagnes publicitaires depuis un seul endroit.',
    description: 'OneBotAds connecte Meta, TikTok, Google, Snapchat et LinkedIn pour vous permettre de créer, suivre et automatiser vos publicités en quelques clics.',
  },
  {
    id: 'connect',
    emoji: '🔗',
    title: 'Connectez votre première plateforme',
    subtitle: 'Pour lancer votre première campagne, reliez un compte publicitaire.',
    description: null,
  },
  {
    id: 'create',
    emoji: '🚀',
    title: 'Créez votre première campagne',
    subtitle: 'Trois façons de publier — choisissez celle qui vous correspond.',
    description: null,
  },
  {
    id: 'ready',
    emoji: '🎯',
    title: 'Vous êtes prêt !',
    subtitle: 'Voici tout ce que vous pouvez faire avec OneBotAds.',
    description: null,
  },
];

const PLATFORMS = [
  { key: 'meta',     label: 'Meta Ads',   bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',   icon: '📘' },
  { key: 'tiktok',   label: 'TikTok Ads', bg: 'bg-pink-50',    border: 'border-pink-200',     text: 'text-pink-700',   icon: '🎵' },
  { key: 'google',   label: 'Google Ads', bg: 'bg-yellow-50',  border: 'border-yellow-200',   text: 'text-yellow-700', icon: '🔍' },
  { key: 'snapchat', label: 'Snapchat',   bg: 'bg-yellow-50',  border: 'border-yellow-300',   text: 'text-yellow-600', icon: '👻' },
  { key: 'linkedin', label: 'LinkedIn',   bg: 'bg-sky-50',     border: 'border-sky-200',      text: 'text-sky-700',    icon: '💼' },
  { key: 'shopify',  label: 'Shopify',    bg: 'bg-green-50',   border: 'border-green-200',    text: 'text-green-700',  icon: '🛍️' },
];

const CREATE_OPTIONS = [
  {
    icon: '🤖',
    title: 'Créer avec l\'IA',
    desc: 'Décrivez votre campagne en langage naturel. L\'IA remplit tout.',
    path: '/dashboard/create-ad-ai',
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    badge: 'Recommandé',
    badgeColor: 'bg-purple-100 text-purple-700',
  },
  {
    icon: '✏️',
    title: 'Créer manuellement',
    desc: 'Remplissez le formulaire avec un contrôle total sur chaque paramètre.',
    path: '/dashboard/create-ad',
    color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
    badge: null,
  },
  {
    icon: '🛍️',
    title: 'Depuis Shopify',
    desc: 'Sélectionnez un produit Shopify et générez une pub en 3 clics.',
    path: '/dashboard/shopify-ads',
    color: 'bg-green-50 border-green-200 hover:bg-green-100',
    badge: null,
  },
];

const FEATURES = [
  { icon: '📊', title: 'Statistiques', desc: 'Performances réelles de toutes vos plateformes en un coup d\'œil.' },
  { icon: '📋', title: 'Campaign Manager', desc: 'Mettez en pause ou reprenez vos campagnes directement depuis OneBotAds.' },
  { icon: '⚡', title: 'Règles automatiques', desc: 'Créez des règles qui ajustent ou stoppent vos campagnes automatiquement.' },
  { icon: '💳', title: 'Wallet & Billing', desc: 'Rechargez votre wallet et gérez votre abonnement depuis Billing.' },
];

const OnboardingFlow = ({ onDismiss, journey = null }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // Parcours adapté (ex. marchand Shopify) : rendu générique piloté par le serveur.
  const isCustomJourney = journey && journey.key !== "default" && Array.isArray(journey.steps) && journey.steps.length > 0;
  const jSteps = isCustomJourney ? journey.steps : null;

  if (isCustomJourney) {
    const cur = jSteps[step];
    const last = step === jSteps.length - 1;
    const goCta = () => { onDismiss(); if (cur.cta?.route) navigate(cur.cta.route); };
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
        <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
          <div className="h-1 bg-gray-100 w-full">
            <div className="h-1 bg-[#221C51] transition-all duration-500" style={{ width: `${((step + 1) / jSteps.length) * 100}%` }} />
          </div>
          <button onClick={onDismiss} className="absolute top-4 right-4 text-xs text-gray-400 hover:text-gray-600 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">Passer</button>
          <div className="flex items-center justify-center gap-2 pt-5">
            {jSteps.map((_, i) => (
              <div key={i} className={`rounded-full transition-all duration-300 ${i === step ? "w-6 h-2 bg-[#221C51]" : i < step ? "w-2 h-2 bg-[#221C51]/40" : "w-2 h-2 bg-gray-200"}`} />
            ))}
          </div>
          <div className="px-8 pt-6 pb-8 text-center">
            <div className="text-5xl mb-3">{cur.emoji}</div>
            <h2 className="text-2xl font-bold text-[#221C51] mb-2">{cur.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto mb-6">{cur.subtitle}</p>
            {cur.cta && (
              <button onClick={goCta} className="px-6 py-2.5 bg-green-600 text-white font-semibold text-sm rounded-xl hover:bg-green-700 transition-colors shadow-md mb-4">
                {cur.cta.label} →
              </button>
            )}
            <div className="flex items-center justify-between mt-2">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} className={`text-sm font-medium px-5 py-2.5 rounded-xl transition-colors ${step === 0 ? "invisible" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}>← Retour</button>
              <button onClick={() => (last ? goCta() : setStep((s) => s + 1))} className="px-6 py-2.5 bg-[#221C51] text-white font-semibold text-sm rounded-xl hover:bg-[#2d2570] transition-colors shadow-md">
                {last ? (cur.cta ? cur.cta.label + " →" : "Terminer") : "Suivant →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      onDismiss();
    } else {
      setStep(s => s + 1);
    }
  };

  const handlePlatformClick = () => {
    onDismiss();
    navigate('/dashboard/connected-platforms');
  };

  const handleCreateClick = (path) => {
    onDismiss();
    navigate(path);
  };

  const handleGetStarted = () => {
    onDismiss();
    navigate('/dashboard/connected-platforms');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden">

        {/* Progress bar */}
        <div className="h-1 bg-gray-100 w-full">
          <div
            className="h-1 bg-[#221C51] transition-all duration-500"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        {/* Skip button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-xs text-gray-400 hover:text-gray-600 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Passer
        </button>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 pt-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step ? 'w-6 h-2 bg-[#221C51]' : i < step ? 'w-2 h-2 bg-[#221C51]/40' : 'w-2 h-2 bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="px-8 pt-6 pb-8">

          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-5xl mb-3">{current.emoji}</div>
            <h2 className="text-2xl font-bold text-[#221C51] mb-2">{current.title}</h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">{current.subtitle}</p>
            {current.description && (
              <p className="text-gray-400 text-xs mt-2 leading-relaxed max-w-sm mx-auto">{current.description}</p>
            )}
          </div>

          {/* Step-specific content */}

          {/* Step 0 — Welcome visual */}
          {step === 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {[
                { icon: '🤖', label: 'IA intégrée' },
                { icon: '📱', label: '5 plateformes' },
                { icon: '⚡', label: 'Règles auto' },
              ].map(item => (
                <div key={item.label} className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-gray-50 border border-gray-100">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="text-xs font-semibold text-[#221C51]">{item.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 1 — Connect platform */}
          {step === 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {PLATFORMS.map(p => (
                <button
                  key={p.key}
                  onClick={handlePlatformClick}
                  className={`flex flex-col items-center gap-2 py-4 rounded-2xl border ${p.bg} ${p.border} hover:shadow-md transition-all duration-150 cursor-pointer`}
                >
                  <span className="text-2xl">{p.icon}</span>
                  <span className={`text-xs font-semibold ${p.text}`}>{p.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — Create options */}
          {step === 2 && (
            <div className="flex flex-col gap-3 mb-6">
              {CREATE_OPTIONS.map(opt => (
                <button
                  key={opt.path}
                  onClick={() => handleCreateClick(opt.path)}
                  className={`flex items-start gap-4 p-4 rounded-2xl border text-left transition-all duration-150 ${opt.color}`}
                >
                  <span className="text-2xl mt-0.5">{opt.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#221C51] text-sm">{opt.title}</span>
                      {opt.badge && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${opt.badgeColor}`}>
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </div>
                  <span className="text-gray-300 text-lg mt-0.5">›</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 3 — Features overview */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {FEATURES.map(f => (
                <div key={f.title} className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                  <span className="text-2xl">{f.icon}</span>
                  <div>
                    <p className="font-semibold text-[#221C51] text-sm">{f.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              className={`text-sm font-medium px-5 py-2.5 rounded-xl transition-colors ${
                step === 0 ? 'invisible' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              ← Retour
            </button>

            {isLast ? (
              <button
                onClick={handleGetStarted}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#221C51] text-white font-semibold text-sm rounded-xl hover:bg-[#2d2570] transition-colors shadow-md"
              >
                Connecter une plateforme →
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#221C51] text-white font-semibold text-sm rounded-xl hover:bg-[#2d2570] transition-colors shadow-md"
              >
                Suivant →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;
